<script lang="ts" setup>
    import {
        isModelAvailable,
        ModelCatalogSource,
        ModelGuarantee,
        type ModelCatalogEntry,
    } from 'models'
    import { MODELS } from './constants'
    import PkLanguageModel from '../chat/PkLanguageModel.vue'
    import PkModelOption from './PkModelOption.vue'
    import { MODEL_CATALOG_KEY } from './modelCatalog'
    import { inject } from 'vue'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const props = defineProps<{
        name?: string
        multiple?: boolean
        /**
         * Declared, and not left to fall through, so a blank can be filtered
         * out: `VvCombobox` appends to its own list every value no option
         * carries, and an empty string is such a value. It drew an empty row
         * in the list and, in a form field that fills an unset value with
         * `''`, an empty chip in the closed field.
         */
        modelValue?: string | string[]
        excludeModels?: string[]
        /**
         * The organization's own catalog. A page that already has it hands it
         * over; otherwise the picker takes the one the app provides. Only a
         * surface with neither, the embeddable widget, falls back to the bare
         * curated list.
         */
        catalog?: ModelCatalogEntry[]
        /**
         * The agent's own privacy flags. A model the gateway cannot route
         * while honouring one of them is hidden, because offering it would
         * promise something nobody can deliver.
         */
        zeroDataRetention?: boolean
        disallowPromptTraining?: boolean
        /**
         * The model already saved on the agent. It is never hidden, however
         * incompatible: a picker that drops the current choice from under
         * the user is worse than one that shows it marked.
         */
        current?: string
        /**
         * Offer the models the organization switched off too, retired ones
         * excepted. For the price dialog, where the choice is a price to copy
         * and not a model to run: a gateway model switched off, or every one
         * of them when the organization does not use the gateway, is still a
         * valid price reference.
         */
        offerDisabled?: boolean
        /**
         * Declared so it cannot fall through, never read. This component owns
         * its option list: it builds it from the catalog, filters it by the
         * agent's guarantees and groups it by source. An undeclared `options`
         * lands on `VvCombobox` through `$attrs`, and `$attrs` is merged last,
         * so a caller's list silently replaced the built one. The agent panel
         * passed the bare curated ids that way, which is why the picker
         * offered models the guarantees exclude, showed no endpoint models at
         * all, and drew the excluded ones without their details.
         */
        options?: never
    }>()

    /**
     * Declared and forwarded by hand, because declaring `modelValue` takes
     * the caller's listener away: Vue drops `onUpdate:modelValue` from the
     * attributes that fall through to the root once the component owns a
     * `modelValue` prop. Left to fall through, every pick emitted into
     * nothing and the field stayed on the model it had.
     */
    const emit = defineEmits<{
        'update:modelValue': [value: string | string[] | undefined]
    }>()

    interface ModelOption {
        label: string
        value: string
        /** Set on an endpoint model, so the option can show where it runs. */
        endpointName?: string
        /** The catalog label, which has no i18n key to look up. */
        catalogLabel?: string
        entry?: ModelCatalogEntry
    }

    /**
     * Without a catalog the option is the bare id: this is the widget and the
     * document dialogs, where the names came from seventy hand-written i18n
     * labels that nothing verified and that a model could ship without.
     */
    const provided = inject(MODEL_CATALOG_KEY, undefined)
    const catalog = computed(() => props.catalog ?? provided?.value)

    /** An empty string is not a choice, and neither is an empty slot. */
    const selection = computed(() =>
        Array.isArray(props.modelValue)
            ? props.modelValue.filter(Boolean)
            : props.modelValue || undefined,
    )

    const builtInOptions = computed<ModelOption[]>(() =>
        MODELS.map((model) => ({ label: model, value: model })),
    )

    /**
     * A model the gateway cannot serve while honouring a flag the agent has
     * switched on. `some` is not one of those: the gateway routes to the
     * providers that honour it, so the model stays with a note.
     */
    const isRefusedByFlags = (entry: ModelCatalogEntry) =>
        (props.zeroDataRetention === true &&
            entry.zdr === ModelGuarantee.None) ||
        (props.disallowPromptTraining === true &&
            entry.noTraining === ModelGuarantee.None)

    /**
     * What stays offered however unavailable: the saved model and whatever
     * the field already holds, fallbacks included. A picker that drops a
     * chosen value from under the user is worse than one that shows it.
     */
    const kept = computed(
        () =>
            new Set(
                [
                    props.current,
                    ...(Array.isArray(selection.value)
                        ? selection.value
                        : [selection.value]),
                ].filter(Boolean),
            ),
    )

    const catalogOptions = computed<ModelOption[]>(() =>
        (catalog.value ?? [])
            .filter(
                (entry) =>
                    kept.value.has(entry.id) ||
                    // Not a choice for this organization: switched off, its
                    // source off, or retired. Still in the catalog, so an
                    // agent already on one keeps it visible.
                    ((props.offerDisabled
                        ? !entry.retired
                        : isModelAvailable(entry)) &&
                        !isRefusedByFlags(entry)),
            )
            .map((entry) => ({
                label: entry.label,
                value: entry.id,
                catalogLabel: entry.label,
                entry,
                endpointName:
                    entry.source === ModelCatalogSource.Byok
                        ? entry.brand
                        : undefined,
            })),
    )

    /**
     * Two groups when the catalog carries endpoint models, one flat list
     * otherwise: a lone "Gateway" heading over every option says nothing.
     */
    const models = computed(() => {
        const excluded = props.excludeModels ?? []
        const all = (
            catalog.value ? catalogOptions.value : builtInOptions.value
        ).filter((option) => !excluded.includes(option.value))
        const own = all.filter((option) => option.endpointName)
        if (!own.length) {
            return all
        }
        const gateway = all.filter((option) => !option.endpointName)
        return [
            { label: $t('label.gatewayModels'), options: gateway },
            { label: own[0].endpointName as string, options: own },
        ]
    })

    /**
     * Both sources on offer: then which one the chosen model runs on is part
     * of the choice, and the closed field names it for a gateway model too.
     */
    const offersBothSources = computed(() =>
        models.value.some((option) => 'options' in option),
    )

    const flatOptions = computed<ModelOption[]>(() =>
        catalog.value ? catalogOptions.value : builtInOptions.value,
    )

    /**
     * The combobox types its slot scope from the `options` prop, which is a
     * union of plain options and groups once the endpoint models are there,
     * so the template reads through these two rather than narrowing inline.
     *
     * A value no option carries comes back as the bare id: `VvCombobox`
     * appends `modelValue` to its own list when nothing in it matches, so the
     * slots receive a string instead of a `ModelOption`. It happens every
     * time a saved model leaves the catalog offered here: the organization
     * switches its endpoint on while the defaults are still gateway models,
     * the gateway retires a model an agent is on. Reading `.value` off that
     * string gave `undefined`, and the field and the list drew a chip with an
     * icon and no name.
     */
    const valueOf = (option: unknown) =>
        typeof option === 'string'
            ? option
            : (option as ModelOption | undefined)?.value

    const optionOf = (option: unknown) => {
        const value = valueOf(option)
        return flatOptions.value.find((item) => item.value === value)
    }

    type SearchableOption =
        ModelOption | string | { label: string; options: ModelOption[] }

    const isGroup = (
        option: SearchableOption,
    ): option is { label: string; options: ModelOption[] } =>
        typeof option === 'object' && 'options' in option

    // The default filter only looks at the label: match the raw id too (e.g.
    // "openai/gpt-5.6-sol", the identifier used by the API), so that a provider
    // name or a version written with dashes finds its models. The list handed
    // over is the combobox's own, so it can carry the bare id of a value no
    // option matches: `'options' in option` on a string throws, which took the
    // search down with it.
    const searchModels = (query: string, options: SearchableOption[]) => {
        const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
        if (!terms.length) {
            return options
        }
        const matches = (option: ModelOption | string) => {
            const haystack = (
                typeof option === 'string'
                    ? option
                    : `${option.label} ${option.value}`
            ).toLowerCase()
            return terms.every((term) => haystack.includes(term))
        }
        return options
            .map((option) =>
                isGroup(option)
                    ? { ...option, options: option.options.filter(matches) }
                    : option,
            )
            .filter((option) =>
                isGroup(option) ? option.options.length : matches(option),
            )
    }
</script>

<template>
    <VvCombobox
        class="pk-language-model-combobox"
        :model-value="selection"
        :options="models"
        :multiple
        :badges="multiple"
        :name
        searchable
        :search-function="searchModels"
        :no-options-label="$t('message.noOptions')"
        :no-results-label="$t('message.noResults')"
        select-hint-label=""
        deselect-hint-label=""
        selected-hint-label=""
        @update:model-value="emit('update:modelValue', $event)">
        <template #value="{ selectedOptions }">
            <template v-if="props.multiple">
                <PkLanguageModel
                    v-for="option in selectedOptions"
                    :key="valueOf(option)"
                    :model-name="valueOf(option)"
                    :label="optionOf(option)?.catalogLabel"
                    :brand-icon="optionOf(option)?.entry?.brandIcon"
                    :endpoint-name="optionOf(option)?.endpointName"
                    :show-source="offersBothSources"
                    :class="{
                        'text-12 gap-4 border border-surface-3 bg-surface px-6 py-3 rounded-md':
                            selectedOptions.length > 1,
                        'text-14 gap-sm': selectedOptions.length === 1,
                    }" />
            </template>
            <PkLanguageModel
                v-else
                :model-name="valueOf(selectedOptions[0])"
                :label="optionOf(selectedOptions[0])?.catalogLabel"
                :brand-icon="optionOf(selectedOptions[0])?.entry?.brandIcon"
                :endpoint-name="optionOf(selectedOptions[0])?.endpointName"
                :show-source="offersBothSources"
                class="text-14 gap-sm" />
        </template>
        <template #option="{ option }">
            <PkModelOption
                v-if="optionOf(option)?.entry"
                :entry="optionOf(option)?.entry"
                stacked
                class="text-14" />
            <PkLanguageModel
                v-else
                :model-name="valueOf(option)"
                :label="optionOf(option)?.catalogLabel"
                :endpoint-name="optionOf(option)?.endpointName"
                class="text-14 gap-sm" />
        </template>
    </VvCombobox>
</template>

<style lang="scss">
    .pk-language-model-combobox {
        // An option here is two lines, and the default padding is sized for
        // one: at --spacing-sm top and bottom the list showed five models
        // before it had to scroll. The dropdown is rendered inside this root,
        // so the token reaches the options without touching other pickers.
        --vv-dropdown-option-padding: var(--spacing-6) var(--spacing-sm);
    }
</style>
